import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import FolderIcon from '@material-ui/icons/Folder';
import { organizationApi } from 'src/api/organizationApi';
import toast from 'react-hot-toast';

const useStyles = makeStyles((theme) => ({
    container: {
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
    },
    title: {
        marginBottom: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.primary,
        fontSize: '0.9rem',
    },
    searchField: {
        marginBottom: theme.spacing(1),
    },
    projectList: {
        flex: 1,
        overflow: 'auto',
        maxHeight: '200px',
    },
    projectItem: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        borderRadius: theme.spacing(1),
        margin: theme.spacing(0.25, 0),
        padding: theme.spacing(0.5, 1),
    },
    projectIcon: {
        color: theme.palette.primary.main,
        fontSize: '18px',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        minHeight: '100px',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
}));

interface Project {
    id: string;
    name: string;
    description?: string;
}

interface EmbeddedProjectSelectorProps {
    onProjectSelect: (project: Project) => void;
}

const EmbeddedProjectSelector: React.FC<EmbeddedProjectSelectorProps> = ({
    onProjectSelect,
}) => {
    const classes = useStyles();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchProjects = async (search?: string) => {
        setLoading(true);
        try {
            const response = await organizationApi.getAllProjects(
                0, // page
                100, // pageSize - fetch a large number to get all projects
                search || '', // search term
                undefined, // columns
                undefined // sortVal
            );
            if (response?.status === 200) {
                setProjects(response.data?.data || response.data || []);
            }
        } catch (error) {
            toast.error('Failed to fetch projects');
            console.error('Error fetching projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
    };

    const handleProjectClick = (project: Project) => {
        onProjectSelect(project);
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchProjects(searchTerm);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    return (
        <Box className={classes.container}>
            <Typography variant="h6" className={classes.title}>
                Which project is this related to?
            </Typography>

            <TextField
                fullWidth
                placeholder="Search projects..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={classes.searchField}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            {loading ? (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                </Box>
            ) : projects.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Typography variant="body1">
                        {searchTerm ? 'No projects found matching your search.' : 'No projects available.'}
                    </Typography>
                </Box>
            ) : (
                <List className={classes.projectList}>
                    {projects.map((project, index) => (
                        <React.Fragment key={project.id}>
                            <ListItem
                                className={classes.projectItem}
                                onClick={() => handleProjectClick(project)}
                                dense
                            >
                                <ListItemIcon>
                                    <FolderIcon className={classes.projectIcon} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={project.name}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                            {index < projects.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default EmbeddedProjectSelector; 